import { Component, computed, effect, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DocumentPermission } from '../../../../core/enums/data.enums';
import { BulkChangeFolderPermissionRequest, FolderModel, UpdateFolderRequest } from '../../../../core/models/data/document.model';
import { DocumentService } from '../../../../core/services/document.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ModalActions } from '../../../../core/enums/base.enums';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { DocumentsConstants } from '../../../../core/constants/documents.constants';
import { DocumentsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { DOCUMENT_PERMISSION_LABELS } from '../../../../core/constants/display-labels.constants';

export interface PermissionUpdateFailure {
    folderName: string;
    message: string;
}

export interface PermissionUpdateResult {
    updated: number;
    failures: PermissionUpdateFailure[];
}

@Component({
    selector: 'ms-document-permissions-modal',
    standalone: true,
    imports: [CommonModule, FontAwesomeModule, EscCloseDirective],
    templateUrl: './document-permissions-modal.component.html',
    styleUrl: './document-permissions-modal.component.scss'
})
export class DocumentPermissionsModalComponent {

    public targets = input<FolderModel[]>([]);

    public submitted = output<PermissionUpdateResult>();
    public cancelled = output<void>();

    public modalId = DocumentsConstants.PERMISSIONS_MODAL_ID();
    public DocumentPermission = DocumentPermission;

    public selectedLevel = signal<DocumentPermission>(DocumentPermission.MemberRead);
    public isSubmitting = signal<boolean>(false);
    public failures = signal<PermissionUpdateFailure[]>([]);

    public isMultiSelect = computed<boolean>(() => this.targets().length > 1);
    public sharedLevel = computed<DocumentPermission | undefined>(() => {
        const ts = this.targets();
        if (ts.length === 0) return undefined;
        const first = ts[0].permission;
        return ts.every(t => t.permission === first) ? first : undefined;
    });

    constructor(private documentService: DocumentService, private bootstrapService: BootstrapService) {
        effect(() => {
            const ts = this.targets();
            const sharedLevel = this.sharedLevel();
            if (ts.length > 0) {
                this.selectedLevel.set(sharedLevel ?? DocumentPermission.MemberRead);
            }
            this.failures.set([]);
        });
    }

    public chooseLevel(level: DocumentPermission): void {
        this.selectedLevel.set(level);
    }

    public async submit(): Promise<void> {
        const ts = this.targets();
        if (ts.length === 0) return;
        this.isSubmitting.set(true);
        this.failures.set([]);

        const newPermission = this.selectedLevel();

        // Multiple folders go through the single bulk endpoint (server cascades to descendants and
        // reports affected/skipped counts). A single folder stays on the per-folder PATCH.
        if (ts.length > 1) {
            await this.submitBulk(ts, newPermission);
        } else {
            await this.submitSingle(ts[0], newPermission);
        }
    }

    private async submitBulk(targets: FolderModel[], permission: DocumentPermission): Promise<void> {
        const request: BulkChangeFolderPermissionRequest = {
            folderIds: targets.map(t => t.folderId),
            permission
        };
        const result = await this.documentService.bulkChangeFolderPermissions(request);
        this.isSubmitting.set(false);

        if (result.success && result.data) {
            this.submitted.emit({ updated: result.data.totalAffected, failures: [] });
            this.close();
            return;
        }

        // A bulk call is all-or-nothing from the client's view: surface one inline failure per target.
        const message = result.message ?? DocumentsMessagingConstants.DOCUMENTS_PERMISSION_UPDATE_FAILED();
        this.failures.set(targets.map(t => ({ folderName: t.name, message })));
    }

    private async submitSingle(folder: FolderModel, permission: DocumentPermission): Promise<void> {
        if (folder.permission === permission) {
            // No-op change — report success without a round trip.
            this.isSubmitting.set(false);
            this.submitted.emit({ updated: 1, failures: [] });
            this.close();
            return;
        }

        const request: UpdateFolderRequest = {
            name: folder.name,
            parentFolderId: folder.parentFolderId,
            permission
        };
        const result = await this.documentService.updateFolder(folder.folderId, request);
        this.isSubmitting.set(false);

        if (result.success) {
            this.submitted.emit({ updated: 1, failures: [] });
            this.close();
            return;
        }

        this.failures.set([{ folderName: folder.name, message: result.message ?? DocumentsMessagingConstants.DOCUMENTS_PERMISSION_UPDATE_FAILED() }]);
    }

    public cancel(): void {
        this.cancelled.emit();
        this.close();
    }

    public close(): void {
        this.failures.set([]);
        this.bootstrapService.manageModal(`#${this.modalId}`, ModalActions.Hide);
    }

    public getLevelLabel(level: DocumentPermission): string {
        return DOCUMENT_PERMISSION_LABELS[level];
    }

    public getLevelDescription(level: DocumentPermission): string {
        switch (level) {
            case DocumentPermission.AdminOnly: return 'Only org admins can see contents.';
            case DocumentPermission.MemberRead: return 'Visible to all members of this organization.';
            case DocumentPermission.Public: return 'Anyone with the link can fetch contents (CDN-served, no auth).';
        }
    }
}
